/**
 * VAT Rates tab — Settings → Product & Services.
 *
 * Shows VAT rates grouped by product group using DataTable's getSubRows
 * tree mode. Parent rows (depth 0) show group name + rate count. Child rows
 * (depth 1) show per-rate fields.
 *
 * Matches legacy VatRatesTab column set:
 * Group level: Name | VAT rates count
 * Rate level: ID | Tax rate (%) | Effective From | Effective To | Description | Status
 *
 * Status logic: active when now ∈ [effective_from, effective_to) with
 * open-ended effective_to treated as infinite (matches legacy isActive).
 *
 * Write operations (create/edit/delete) require the numeric product_group_id
 * which is NOT present in /product-groups/vat-rates. They are wired but the
 * USER triggers them — this tab is display + read-only data fetching only.
 */
import { useMemo } from 'react';
import { Receipt } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { formatDate } from '@/lib/format';
import { useAdminVatRates, type VatRate, type ProductGroupWithVatRates } from '@/api/products-admin';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VatTreeNode {
  _type: 'group' | 'rate';
  // Group fields (depth 0)
  product_group_code?: string;
  product_group_name?: string;
  _rateCount?: number;
  subRows?: VatTreeNode[];
  // Rate fields (depth 1)
  id?: number;
  tax_rate?: number;
  effective_from?: string;
  effective_to?: string | null;
  description?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isRateActive(rate: VatRate): boolean {
  const now = new Date();
  const from = new Date(rate.effective_from);
  if (isNaN(from.getTime())) return false;
  if (now < from) return false;
  if (rate.effective_to) {
    const to = new Date(rate.effective_to);
    if (!isNaN(to.getTime()) && now >= to) return false;
  }
  return true;
}

function buildVatTree(groups: ProductGroupWithVatRates[]): VatTreeNode[] {
  return groups.map((g) => ({
    _type: 'group' as const,
    product_group_code: g.product_group_code,
    product_group_name: g.product_group_name,
    _rateCount: g.vat_rates?.length ?? 0,
    subRows: (g.vat_rates ?? []).map((r) => ({
      _type: 'rate' as const,
      id: r.id,
      tax_rate: r.tax_rate,
      effective_from: r.effective_from,
      effective_to: r.effective_to,
      description: r.description,
      // Store active status — derived once, not re-evaluated per render
      _isActive: isRateActive(r),
    })),
  }));
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const COLUMNS: ColumnDef<VatTreeNode & { _isActive?: boolean }, unknown>[] = [
  {
    id: 'name',
    header: 'Product Group',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') {
        return (
          <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>
            {node.product_group_name}
          </span>
        );
      }
      return (
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          #{node.id}
        </span>
      );
    },
  },
  {
    id: 'rate_or_count',
    header: 'Tax Rate / Count',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') {
        return (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {node._rateCount} {node._rateCount === 1 ? 'rate' : 'rates'}
          </span>
        );
      }
      return (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 'var(--vn-font-weight-medium)' }}>
          {node.tax_rate !== undefined
            ? `${(node.tax_rate * 100).toFixed(1)}%`
            : '—'}
        </span>
      );
    },
  },
  {
    id: 'effective_from',
    header: 'Effective From',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return formatDate(node.effective_from);
    },
  },
  {
    id: 'effective_to',
    header: 'Effective To',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return node.effective_to ? formatDate(node.effective_to) : '—';
    },
  },
  {
    id: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const node = row.original;
      if (node._type === 'group') return null;
      return node.description || '—';
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const node = row.original as VatTreeNode & { _isActive?: boolean };
      if (node._type === 'group') return null;
      return node._isActive ? (
        <StatusPill tone="success">Active</StatusPill>
      ) : (
        <StatusPill tone="neutral">Inactive</StatusPill>
      );
    },
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function VatRatesTab() {
  const { data: groups = [], isLoading, isError, refetch } = useAdminVatRates();

  const treeData = useMemo(() => buildVatTree(groups), [groups]);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load VAT rates"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <Card flat>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 24 }} />
          ))}
        </CardBody>
      </Card>
    );
  }

  return (
    <DataTable
      columns={COLUMNS}
      data={treeData}
      getRowId={(row) =>
        row._type === 'group'
          ? `vg-${row.product_group_code}`
          : `vr-${row.id}`
      }
      getSubRows={(row) => row.subRows}
      defaultAllExpanded={false}
      emptyState={
        <EmptyState
          icon={<Receipt size={24} />}
          title="No VAT rates found"
          description="No VAT rates have been configured yet."
        />
      }
    />
  );
}
